import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Typography, Modal, Slider, App, Form, Input, Checkbox, SliderSingleProps } from 'antd'
import { PiBatteryEmptyBold, PiBatteryFullBold } from 'react-icons/pi'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { sendEnergyScoreFeedback } from '../../services/api'
import { EnergyModeConfig } from '../../types'
import { getEnergyModeConfig, getEnergyModeName } from '../../services/energy-mode-config'

const { Text } = Typography

interface FeedbackModalProps {
  currentScore: number
  currentEnergyModeConfig: EnergyModeConfig
  energyModeNameMap: Record<EnergyMode, string>
  onFeedbackLinkClick?: (location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state') => void
  onFeedbackSubmit?: (feedbackReason: string, userSuggestedScore: number | undefined, feedbackText: string, timeToSubmitMs: number,
    submissionMethod?: 'quick_rating' | 'detailed_form' | 'inline_feedback', allowContact?: boolean, reportedEnergyMode?: EnergyMode) => void
  onFeedbackSuccess?: () => void
}

export interface FeedbackModalRef {
  openModal: () => void
}

const FeedbackModal = forwardRef<FeedbackModalRef, FeedbackModalProps>(({
  currentScore,
  currentEnergyModeConfig,
  energyModeNameMap,
  onFeedbackLinkClick,
  onFeedbackSubmit,
  onFeedbackSuccess,
}, ref) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newScore, setNewScore] = useState(currentScore)
  const [isSending, setIsSending] = useState(false)
  const [feedbackStartTime, setFeedbackStartTime] = useState<number>(0)

  // Get user's selected energy mode config
  const userSelectedModeConfig = useMemo(() => {
    return getEnergyModeConfig(newScore, formatMessage)
  }, [newScore, formatMessage])

  // Create slider marks with energy mode names at exact breakpoints
  const sliderMarks = useMemo(() => {
    const mode = getEnergyModeName(currentScore, formatMessage)
    const marks: Record<number, string | { label: React.ReactNode }> = {
      [currentScore]: energyModeNameMap[mode.toLowerCase()],
    }
    // Add battery icons at 0 and 100
    marks[0] = { label: <PiBatteryEmptyBold size={20} /> }
    marks[100] = { label: <PiBatteryFullBold size={20} /> }
    return marks
  }, [energyModeNameMap, currentScore, formatMessage])

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setNewScore(currentScore)
      setIsModalVisible(true)
      setFeedbackStartTime(Date.now())

      // Track feedback link click when modal opens
      if (onFeedbackLinkClick) {
        onFeedbackLinkClick('energy_gauge')
      }
    },
  }))

  // Reset and set form values when modal opens
  useEffect(() => {
    if (isModalVisible) {
      // Set form values when modal opens - use setTimeout to ensure Form is mounted
      setTimeout(() => {
        form.setFieldsValue({ score: currentScore, feedback: '', allowContact: false })
        setNewScore(currentScore)
      }, 0)
    }
  }, [isModalVisible, currentScore, form])

  // Sync form score value with newScore state for display (when form value changes)
  useEffect(() => {
    if (isModalVisible) {
      const formScore = form.getFieldValue('score')
      if (formScore !== undefined && formScore !== newScore) {
        setNewScore(formScore)
      }
    }
  }, [isModalVisible, form, newScore])

  const handleScoreUpdate = async () => {
    try {
      setIsSending(true)
      const values = await form.validateFields()

      // Use the user's actual feedback text as the reason
      const userScore = values.score as number
      const feedbackText = values.feedback || ''

      // Track feedback submission with user's actual text as the reason
      if (onFeedbackSubmit) {
        const timeToSubmit = Date.now() - feedbackStartTime
        const reportedEnergyMode = getEnergyMode(userScore)
        onFeedbackSubmit(
          feedbackText, // Use actual user feedback text as the reason
          userScore,
          feedbackText, // Also keep it as feedbackText for backwards compatibility
          timeToSubmit,
          'detailed_form',
          values.allowContact || false, // Pass the checkbox value for contact consent
          reportedEnergyMode // Pass the energy mode the user suggested
        )
      }

      // Update the API call to include the new fields
      await sendEnergyScoreFeedback(userScore, {
        originalScore: currentScore,
        userPerception: userScore,
        feedback: values.feedback,
        allowContact: values.allowContact,
      })

      notification.success({
        message: formatMessage({ id: 'energyLevel.feedbackSuccess' }),
      })
      setIsSending(false)
      // Reset form before closing
      form.resetFields()
      form.setFieldsValue({ score: currentScore, feedback: '', allowContact: false })
      setNewScore(currentScore)
      setIsModalVisible(false)

      // Trigger data refetch to get the updated energy score
      if (onFeedbackSuccess) {
        onFeedbackSuccess()
      }
    } catch (error) {
      setIsSending(false)
      notification.error({
        message: formatMessage({ id: 'energyLevel.feedbackError' }),
      })
    }
  }

  const handleCancel = (e?: React.MouseEvent | React.KeyboardEvent) => {
    // Prevent accidental closure from slider events
    if (e && e.target && (e.target as HTMLElement).closest?.('.ant-slider')) {
      return
    }
    // Reset form and state before closing
    form.resetFields()
    form.setFieldsValue({ score: currentScore, feedback: '', allowContact: false })
    setNewScore(currentScore)
    setIsModalVisible(false)
  }

  const tooltipSliderFormatter: NonNullable<SliderSingleProps['tooltip']>['formatter'] =
    (value) => getEnergyModeName(value as number, formatMessage)

  return (
    <Modal
      title={<IntlMessages id="energyLevel.helpImproveTitle" />}
      open={isModalVisible}
      onOk={handleScoreUpdate}
      onCancel={handleCancel}
      okText={<IntlMessages id="energyLevel.submitFeedback" />}
      cancelText={<IntlMessages id="app.cancel" />}
      className="energy-score-feedback-modal"
      confirmLoading={isSending}
      width={600}
      wrapClassName="energy-modal-wrapper"
      maskClosable={false} // Prevent closing by clicking outside modal
    >
      <div className="header-separator" />

      <div className="feedback-modal-content">
        <div className="feedback-description-section">
          <Text>
            <IntlMessages id="energyLevel.helpImproveDescription" />
          </Text>
        </div>

        <div className="feedback-algorithm-section">
          <Text className="feedback-text-small">
            <IntlMessages id="energyLevel.algorithmThinksCurrent" />{' '}
            <Text
              strong
              className="energy-level-color-text"
              style={{
                ['--energy-level-color' as string]: currentEnergyModeConfig.primaryColor,
              }}
            >
              {currentEnergyModeConfig.name}.
            </Text>
          </Text>
        </div>

        {isModalVisible && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{ score: currentScore, feedback: '', allowContact: false }}
            preserve={false}
            key={'form-visible'}
          >
            <Form.Item className="energy-question-form-item">
              <Text className="energy-feedback-question">
                <IntlMessages id="energyLevel.actualEnergyQuestion" />
              </Text>
            </Form.Item>

            <Form.Item
              name="score"
              rules={[{ required: true, message: formatMessage({ id: 'energyLevel.scoreRequired' }) }]}
              className="energy-slider-form-item"
              getValueFromEvent={(value) => value}
            >
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.score !== currentValues?.score}>
                {({ getFieldValue }) => {
                  const scoreValue = getFieldValue('score') ?? currentScore
                  return (
                    <div className="energy-score-slider-container">
                      <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={scoreValue}
                        onChange={(value) => {
                        // Update form value - Form.Item will handle the rest
                          form.setFieldsValue({ score: value })
                          // Also update state immediately for display
                          setNewScore(value)
                        }}
                        marks={sliderMarks}
                        tooltip={{formatter: tooltipSliderFormatter}}
                      />
                    </div>
                  )
                }}
              </Form.Item>
            </Form.Item>

            <div className="feedback-selected-section">
              <Text className="feedback-text-small">
                <IntlMessages id="energyLevel.selectedEnergyLevel" />{' '}
                <Text
                  strong
                  className="energy-level-color-text"
                  style={{
                    ['--energy-level-color' as string]: userSelectedModeConfig.primaryColor,
                  }}
                >
                  {userSelectedModeConfig.name}.
                </Text>
              </Text>
            </div>

            <Form.Item
              name="feedback"
              label={
                <span>
                  {formatMessage({ id: 'energyLevel.yourFeedbackLabel' })}
                  {' '}
                  <span className="feedback-label-optional">{formatMessage({ id: 'energyLevel.optional' })}</span>
                </span>
              }
              className="energy-feedback-form-item"
            >
              <Input.TextArea
                rows={4}
                placeholder={formatMessage({ id: 'energyLevel.feedbackPlaceholder' })}
                maxLength={1000}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="allowContact"
              valuePropName="checked"
              className="energy-contact-form-item"
            >
              <div className="contact-checkbox-container">
                <Checkbox>
                  <Text>
                    <IntlMessages id="energyLevel.openToContact" />
                  </Text>
                </Checkbox>
              </div>
            </Form.Item>
          </Form>
        )}
      </div>
    </Modal>
  )
})

FeedbackModal.displayName = 'FeedbackModal'

export default FeedbackModal
